<script lang="ts">
  import Board, { collapsing } from '~/board/Board.svelte';
  import GameContext from '~/store/GameContext.svelte';
  import DimensionsContext from '~/utils/DimensionsContext.svelte';
  import Word from '~/components/Word.svelte';
  import Gauge from '~/gauge/classic/Gauge.svelte';
  import { ClassicTutorial } from './ClassicTutorial.svelte';
  import { scoreWord } from '~/algorithms/letters';
  import { onboarded } from '~/tutorial/state.svelte';
  import HomeIcon from '~icons/mdi/home';
  import FastForwardIcon from '~icons/mdi/fast-forward';
  import PlayIcon from '~icons/mdi/play';
  import Star from '~/icons/Star.svelte';
  import {
    crossing,
    growAnimation,
    sendWordAnimation,
    scoreWordAnimation,
  } from '~/animations/crossfade.svelte';
  import { animationSpeed, delay } from '~/animations/utils.svelte';
  import { starInAnimation, starOutAnimation } from '~/animations.svelte';
  import { setStars, clearStars } from '~/board/Stars.svelte';
  import { getSwapBonus } from '~/algorithms/gameLogic';
  import { tick } from 'svelte';
  import type { Coord, HighlightColors, Match } from '~/types';
  import { lettersToBoard, wordToString, type TileSpec } from '~/board/utils';
  import ActionButton from '~/components/ActionButton.svelte';
  import { fly } from 'svelte/transition';

  // Single 4-col × 5-row board, reused across all steps. After each step's
  // word is matched, ClassicGame.replaceLetters refills empty cells from
  // ClassicTutorial.letterQueue (which we set per-step below). Sampling order
  // is col 0 row 0, row 1, ..., col 1 row 0, ... — but each col is reversed
  // before sampling, so within a column the queue fills bottom-up. After
  // refill the column is reversed back, so the queue's first letter for a
  // given column ends up at the BOTTOM-most empty slot.
  //
  // Layout walkthrough:
  //   row 3 starts as [G, M, A, E] → step 1 swaps M↔A (cols 1↔2) → GAME.
  //   col 0 row 3 was G; after refill col 0 = [R, G, N, A, E] → step 2 swaps
  //   rows 1↔3 (G↔A, non-adjacent) → [R, A, N, G, E] = RANGE (full column).
  //   row 4 col 1 has the 2× V; row 4 = [M, V*2, O, E] → step 3 swaps
  //   V↔O (cols 1↔2) → [M, O, V*2, E] = MOVE.
  //   step 3's refill drops TH at col 0 row 0 → col 0 = [TH, N, I, K, X];
  //   step 4 swaps N↔I (rows 1↔2) → [TH, I, N, K, X] = THINK.
  const initialBoard = lettersToBoard([
    ['G', 'N', 'A', 'G', 'E'],
    ['Z', 'K', 'X', 'M', { letter: 'V', multiplier: 2 }],
    ['Z', 'J', 'Y', 'A', 'O'],
    ['Z', 'Q', 'K', 'E', 'E'],
  ]);

  type Step = {
    hint: string;
    allowedSwap?: [Coord, Coord];
    word?: { coords: Coord[]; axis: 'row' | 'col'; highlight: HighlightColors };
    // Letters used to refill empty cells after the word clears. Sampled in
    // order: col 0 first, then col 1, etc.; each column reversed so queue[0]
    // for that column lands at the lowest empty row.
    refillQueue: TileSpec[];
  };

  const STEPS: Step[] = [
    {
      hint: 'Swap <strong>M</strong> and <strong>A</strong> along the row to spell GAME. Adjacent swaps cost 1 ⭐.',
      allowedSwap: [
        [1, 3],
        [2, 3],
      ],
      word: {
        coords: [
          [0, 3],
          [1, 3],
          [2, 3],
          [3, 3],
        ],
        axis: 'row',
        highlight: 'green',
      },
      // One empty per col at row 3. After refill: col 0 row 0 = R, others = Z.
      refillQueue: ['R', 'Z', 'Z', 'Z'],
    },
    {
      hint: 'Tiles can swap any distance — non-adjacent swaps cost 2 ⭐. Swap <strong>G</strong> and <strong>A</strong> in the first column to spell RANGE.',
      allowedSwap: [
        [0, 1],
        [0, 3],
      ],
      word: {
        coords: [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
          [0, 4],
        ],
        axis: 'col',
        highlight: 'orange',
      },
      // Col 0 fully cleared; refill 5 letters. Sample order [M, X, K, I, N]
      // → col 0 top-to-bottom becomes [N, I, K, X, M].
      refillQueue: ['M', 'X', 'K', 'I', 'N'],
    },
    {
      hint: "Multiplier tiles boost the entire word's score. Swap <strong>V</strong> and <strong>O</strong> to spell MOVE through the 2× tile.",
      allowedSwap: [
        [1, 4],
        [2, 4],
      ],
      word: {
        coords: [
          [0, 4],
          [1, 4],
          [2, 4],
          [3, 4],
        ],
        axis: 'row',
        highlight: 'green',
      },
      // One empty per col at row 4. col 0 gets TH at top so step 4 can spell
      // THINK; others get filler Z.
      refillQueue: ['TH', 'Z', 'Z', 'Z'],
    },
    {
      hint: 'Some tiles hold two letters. Swap <strong>N</strong> and <strong>I</strong> to spell THINK using the <strong>TH</strong> tile.',
      allowedSwap: [
        [0, 1],
        [0, 2],
      ],
      word: {
        coords: [
          [0, 0],
          [0, 1],
          [0, 2],
          [0, 3],
        ],
        axis: 'col',
        highlight: 'green',
      },
      // Last step — tutorial ends right after; refill letters never display.
      refillQueue: [],
    },
    {
      hint: "The game ends if you ever run out of swaps. Now you're ready to play!",
      allowedSwap: undefined,
      word: undefined,
      refillQueue: [],
    },
  ];

  const tutorialGame = new ClassicTutorial(initialBoard);
  // Tutorial boards are 4 cols × 5 rows; the parent context's aspect (6/7 on
  // /classic) would stretch tiles horizontally. Override with 4/5 sized to fit
  // the actual .board-area (which is flex:1 between the prompt/gauge/word-
  // display/buttons), not the parent's full classic-board allotment.
  //
  // DimensionsContext.setContext only fires once at setup, so we need a stable
  // object reference whose inner $state properties mutate in place — otherwise
  // consumers stay pinned to the initial 0×0 snapshot.
  const TUTORIAL_ASPECT = 4 / 5;
  let boardAreaWidth = $state(0);
  let boardAreaHeight = $state(0);
  const tutorialDims = $state({
    board: { width: 0, height: 0 },
    aspect: TUTORIAL_ASPECT,
  });
  $effect(() => {
    const aw = boardAreaWidth;
    const ah = boardAreaHeight;
    if (!aw || !ah) return;
    if (aw / ah > TUTORIAL_ASPECT) {
      tutorialDims.board.height = ah;
      tutorialDims.board.width = ah * TUTORIAL_ASPECT;
    } else {
      tutorialDims.board.width = aw;
      tutorialDims.board.height = aw / TUTORIAL_ASPECT;
    }
  });

  let step = $state(0);
  // svelte-ignore state_referenced_locally
  let allowedSwap = $state<[Coord, Coord] | undefined>(STEPS[step].allowedSwap);

  const handleScore = async (): Promise<boolean> => {
    const s = STEPS[step];
    allowedSwap = undefined;

    if (s.word) {
      const tiles = s.word.coords.map(([i, j]) => tutorialGame.board[i][j]);
      for (const tile of tiles) {
        tutorialGame.highlighted[tile.id] = s.word.highlight;
      }
      await tick();

      const { score, stars } = scoreWord(tiles, 'classic');
      const match = {
        word: tiles,
        coords: s.word.coords,
        axis: s.word.axis,
        score,
        length: tiles.reduce((n, t) => n + t.letter.length, 0),
        intersectingIds: [],
        stars,
      } as unknown as Match;

      tutorialGame.latestWord = match;
      tutorialGame.words++;
      tutorialGame.score += score;
      await crossing.launch(match, {}, () => tutorialGame.removeLetters(match));

      // Mirror the real game: apply the swap bonus to game.swaps immediately so
      // the gauge ticks up, then play the in-board star animation in parallel
      // with the word's send-to-score animation.
      const swapBonus = getSwapBonus(match, 0);
      tutorialGame.swaps += swapBonus;

      const animateStars = async () => {
        if (swapBonus === 0) return;
        await growAnimation.settled();
        setStars({
          stars: swapBonus,
          coords: [
            [match.coords[0][0], match.coords[0][1]],
            [
              match.coords[match.coords.length - 1][0],
              match.coords[match.coords.length - 1][1],
            ],
          ],
        });
        await starInAnimation.settled();
        clearStars();
        await starOutAnimation.settled();
      };

      await delay(50);
      await Promise.all([sendWordAnimation.settled(), animateStars()]);
      await scoreWordAnimation.settled();
    }

    // Seed the deterministic refill before collapseBoard runs replaceLetters.
    tutorialGame.letterQueue = [...s.refillQueue];

    // Mirror the real game: signal collapse so dropIn animation params are
    // valid, then collapse and let the new tiles settle before advancing.
    collapsing.current = tutorialGame.board.map(
      (col) => tutorialGame.dims[1] - col.filter((t) => t).length,
    );
    await tick();
    await tutorialGame.collapseBoard();
    await delay(animationSpeed.ms);
    collapsing.current = [];
    await tick();

    if (step < STEPS.length - 1) {
      step++;
      tutorialGame.highlighted = {};
      tutorialGame.latestWord = undefined;
      allowedSwap = STEPS[step].allowedSwap;
      tutorialGame.disabled = false;
    }
    return false;
  };

  const handleEndTurn = async () => {
    tutorialGame.disabled = false;
    return false;
  };

  const handleSkip = () => {
    onboarded.current = true;
  };
</script>

<div class="onboarding">
  <p class="prompt">
    {#key step}
      <span
        in:fly={{ y: 30, duration: 300, delay: 180 }}
        out:fly={{ y: -30, duration: 180 }}
      >
        {#if true}
          {@const segments = (STEPS[step]?.hint ?? '').split('⭐')}
          {#each segments as segment, i}
            {@html segment}
            {#if i < segments.length - 1}
              <Star size="1em" />
            {/if}
          {/each}
        {/if}
      </span>
    {/key}
  </p>
  <DimensionsContext dims={tutorialDims as any}>
    <GameContext game={tutorialGame}>
      <Gauge />
      <div
        class="board-area"
        bind:clientWidth={boardAreaWidth}
        bind:clientHeight={boardAreaHeight}
      >
        <Board
          bind:disabled={tutorialGame.disabled}
          {allowedSwap}
          {handleScore}
          {handleEndTurn}
        />
      </div>
      <div class="word-display">
        {#if tutorialGame.latestWord}
          {#key wordToString(tutorialGame.latestWord)}
            <Word
              word={tutorialGame.latestWord.word}
              highlighted={STEPS[step]?.word?.highlight}
            />
          {/key}
        {/if}
      </div>
    </GameContext>
  </DimensionsContext>
  <div class="button-row">
    <ActionButton href="/" outlink={false}><HomeIcon />Home</ActionButton>
    <ActionButton onClick={handleSkip}>
      {#if step === STEPS.length - 1}
        Play
        <PlayIcon />
      {:else}
        Skip tutorial
        <FastForwardIcon />
      {/if}
    </ActionButton>
  </div>
</div>

<style lang="scss">
  .onboarding {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    gap: 0.5rem;
  }

  .prompt {
    position: relative;
    height: 9ex;
    max-width: 30em;
    margin-top: 0;
    width: 100%;
    text-align: center;
    font-size: 1rem;
    overflow: hidden;

    span {
      position: absolute;
      left: 0;
      right: 0;
    }

    :global(svg) {
      vertical-align: -0.15em;
    }
  }

  .board-area {
    flex: 1;
    width: 100%;
    min-height: 0;
  }

  .word-display {
    position: relative;
    width: 100%;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .button-row {
    margin-top: 1rem;
    display: flex;
    gap: 0.5rem;
  }
</style>
